import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { ArrowLeft, Phone, Mail, Cog, Zap, Gauge, Weight, Ruler, Fuel, MessageSquare, KeyRound } from "lucide-react";
import useSEO from "../hooks/useSEO";
import { api } from "../lib/api";
import { qjStatus, QJ_KEY_SPECS, QJ_FALLBACK_IMG, swatchBg, QJ_BTW, qjPrijs, qjCatByLabel, qjCatBySlug } from "../lib/qj";
import MotoGallery from "../components/MotoGallery";
import QJInquiryModal from "../components/QJInquiryModal";

const ICONS = { cilinderinhoud: Cog, vermogen: Zap, koppel: Gauge, gewicht: Weight, zithoogte: Ruler, tankinhoud: Fuel };

export default function QJDetail() {
  const { id } = useParams();
  const location = useLocation();
  const [m, setM] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [contact, setContact] = useState({ telefoon: "0485 74 15 24", email: "TTmotorsports@outlook.com" });
  const [kleur, setKleur] = useState(0);
  const [inquiry, setInquiry] = useState(null);

  useEffect(() => {
    api.get(`/qj-models/${id}`).then((r) => setM(r.data)).catch(() => setNotFound(true));
    api.get("/settings").then((r) => setContact((c) => ({ telefoon: r.data.telefoon || c.telefoon, email: r.data.email || c.email }))).catch(() => {});
  }, [id]);

  useSEO({
    title: m ? `${m.naam} — QJ Motor | TT Motorsports` : "QJ Motor — TT Motorsports",
    description: m ? `${m.naam}: ${m.korte_omschrijving || ""} ${m.cilinderinhoud}, ${m.vermogen}. Prijs ${qjPrijs(m.prijs)}. Bij TT Motorsports Torhout.` : "",
  });

  if (notFound) {
    return (
      <div className="pt-40 pb-32 text-center bg-[#0A0A0B]" data-testid="qj-notfound">
        <p className="heading-xl text-4xl text-white mb-4">Model niet gevonden</p>
        <Link to="/qj-motor" className="text-[#E10600] font-display uppercase">← Terug naar QJ Motor</Link>
      </div>
    );
  }
  if (!m) return <div className="pt-40 pb-40 text-center text-[#9A9CA3] font-mono-tt bg-[#0A0A0B]">Laden...</div>;

  const st = qjStatus(m.beschikbaarheid);
  const backCat = qjCatBySlug(location.state?.cat) || qjCatByLabel((m.categorieen || [])[0] || m.categorie);
  const keySpecs = QJ_KEY_SPECS.filter((s) => m[s.key]);
  const allSpecs = [...keySpecs.map((s) => ({ label: s.label, waarde: m[s.key] })), ...(m.specs || [])];
  const tel = contact.telefoon.replace(/\s/g, "");

  return (
    <div data-testid="qj-detail-page" className="pt-28 pb-24 bg-[#0A0A0B]">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8">
        <div className="flex flex-wrap items-center gap-2 text-[#9A9CA3] font-display uppercase text-sm font-600 mb-8" data-testid="qj-breadcrumb">
          <Link to="/qj-motor" data-testid="qj-back" className="inline-flex items-center gap-2 hover:text-[#E10600] transition-colors"><ArrowLeft size={16} /> QJ Motor</Link>
          {backCat && (<>
            <span className="text-[#5A5C63]">/</span>
            <Link to={`/qj-motor/categorie/${backCat.slug}`} data-testid="qj-back-cat" className="hover:text-[#E10600] transition-colors">{backCat.label}</Link>
          </>)}
        </div>

        <div className="grid lg:grid-cols-[1.25fr_1fr] gap-10 lg:gap-14">
          <div><MotoGallery images={m.images?.length ? m.images : [QJ_FALLBACK_IMG]} alt={m.naam} /></div>

          <div>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span data-testid="qj-detail-status" className={`font-display uppercase text-xs font-700 tracking-wider px-3 py-1.5 ${st.cls}`}>{st.label}</span>
              {m.categorie && <span className="font-mono-tt text-xs text-[#E10600] uppercase tracking-widest">{m.categorie}</span>}
            </div>
            <h1 data-testid="qj-detail-name" className="heading-xl text-4xl md:text-5xl lg:text-6xl text-white mb-4">{m.naam}</h1>
            {m.korte_omschrijving && <p className="text-[#B8B9BE] mb-6 leading-relaxed">{m.korte_omschrijving}</p>}
            <p className="font-mono-tt text-[11px] uppercase tracking-widest text-[#5A5C63]">Verkoopprijs</p>
            <p className="mb-8">
              <span data-testid="qj-detail-price" className="font-display font-800 text-4xl md:text-5xl text-white">{qjPrijs(m.prijs)}</span>
              {m.prijs != null && <span data-testid="qj-detail-price-info" className="text-[#9A9CA3] text-sm ml-3">{QJ_BTW}</span>}
            </p>

            {keySpecs.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-[#2A2B31] border border-[#2A2B31] mb-8" data-testid="qj-key-specs">
                {keySpecs.map((s) => { const I = ICONS[s.key]; return (
                  <div key={s.key} className="bg-[#141518] p-4">
                    <div className="flex items-center gap-2 text-[#9A9CA3] mb-1"><I size={14} className="text-[#E10600]" /><span className="font-mono-tt text-[10px] uppercase tracking-wider">{s.label}</span></div>
                    <p className="font-display font-700 text-white text-base md:text-lg">{m[s.key]}</p>
                  </div>
                ); })}
              </div>
            )}

            {m.kleuren?.length > 0 && (
              <div className="mb-8" data-testid="qj-colors">
                <p className="font-mono-tt text-[11px] uppercase tracking-widest text-[#9A9CA3] mb-3">Beschikbare kleuren — <span className="text-white">{m.kleuren[kleur]?.naam}</span></p>
                <div className="flex flex-wrap gap-3">
                  {m.kleuren.map((k, i) => (
                    <button key={i} onClick={() => setKleur(i)} title={k.naam} data-testid={`qj-color-${i}`}
                      className={`w-10 h-10 rounded-full border-2 transition-transform hover:scale-110 ${kleur === i ? "border-white ring-2 ring-[#E10600] ring-offset-2 ring-offset-[#0A0A0B] scale-110" : "border-[#2A2B31]"}`}
                      style={{ background: swatchBg(k) }} />
                  ))}
                </div>
              </div>
            )}

            <button onClick={() => setInquiry("testrit")} data-testid="qj-testrit-btn"
              className="w-full bg-[#E10600] hover:bg-[#B80500] text-white font-display uppercase font-800 tracking-wider px-6 py-5 flex items-center justify-center gap-2 transition-colors mb-3">
              <KeyRound size={18} /> Vraag een testrit aan
            </button>
            <button onClick={() => setInquiry("info")} data-testid="qj-inquire-btn"
              className="w-full border border-white/70 hover:bg-white hover:text-[#0A0A0B] text-white font-display uppercase font-700 tracking-wider px-6 py-4 flex items-center justify-center gap-2 transition-colors mb-3">
              <MessageSquare size={17} /> Informeer naar deze motor
            </button>
            <div className="grid sm:grid-cols-2 gap-3">
              <a href={`tel:${tel}`} data-testid="qj-detail-phone" className="border border-[#2A2B31] hover:border-white text-white px-4 py-4 flex items-center justify-center gap-2 font-display uppercase font-700 text-sm transition-colors"><Phone size={16} /> {contact.telefoon}</a>
              <a href={`mailto:${contact.email}?subject=${encodeURIComponent("Interesse QJ Motor: " + m.naam)}`} data-testid="qj-detail-email" className="border border-[#2A2B31] hover:border-white text-white px-4 py-4 flex items-center justify-center gap-2 font-display uppercase font-700 text-sm transition-colors"><Mail size={16} /> E-mail ons</a>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1.25fr_1fr] gap-10 lg:gap-14 mt-16 pt-12 border-t border-[#2A2B31]">
          <div>
            {m.beschrijving && (<>
              <h2 className="heading-xl text-2xl text-white mb-4">Over deze motor</h2>
              <p data-testid="qj-detail-description" className="text-[#B8B9BE] leading-relaxed whitespace-pre-line">{m.beschrijving}</p>
            </>)}
          </div>
          {allSpecs.length > 0 && (
            <div data-testid="qj-spec-table">
              <h2 className="heading-xl text-2xl text-white mb-4">Technische specificaties</h2>
              <dl className="border border-[#2A2B31]">
                {allSpecs.map((s, i) => (
                  <div key={i} className={`grid grid-cols-2 gap-4 px-4 py-3 ${i % 2 ? "bg-[#0F1012]" : "bg-[#141518]"}`}>
                    <dt className="font-mono-tt text-xs uppercase tracking-wider text-[#9A9CA3]">{s.label}</dt>
                    <dd className="text-white text-sm">{s.waarde}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </div>
      {inquiry && <QJInquiryModal model={m} mode={inquiry} onClose={() => setInquiry(null)} />}
    </div>
  );
}
